import moment from "moment";

/** `MMM D` (current year) / `MMM D, YYYY` (other years) — spec §5 shared
 *  behavior. Accepts ISO timestamps and `YYYY-MM-DD` dates. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  if (!m.isValid()) return "—";
  return m.year() === moment().year()
    ? m.format("MMM D")
    : m.format("MMM D, YYYY");
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  if (!m.isValid()) return "—";
  return m.year() === moment().year()
    ? m.format("MMM D, h:mm A")
    : m.format("MMM D, YYYY, h:mm A");
}

/**
 * An actor as a person, not as the wire value.
 *
 * The server stamps a human as `admin:wilson@autumnplatform.com`: the prefix
 * is how the event log tells a person from `system:solver` or `auto:seed`,
 * and everything from the `@` on is just the address's domain — an email is
 * always what follows an `@`, so it never identifies anyone here. Both are
 * dropped for display: the local part is the name.
 *
 * Machine actors pass through untouched: for them the prefix is the meaning.
 */
export function fmtActor(actor: string | null | undefined): string {
  if (!actor) return "";
  const named = actor.startsWith("admin:")
    ? actor.slice("admin:".length)
    : actor;
  const at = named.indexOf("@");
  return at > 0 ? named.slice(0, at) : named;
}

/**
 * Whether this actor is the platform rather than a person — the same prefix
 * convention `fmtActor` reads, asked as a question.
 *
 * Only `system:` and `auto:` are treated as machines. An actor we cannot place
 * — a bare string, or null on a note that predates the stamping — is NOT
 * claimed as one: a surface that withholds a person's writing is worse than
 * one that shows a machine's, so the unknown case falls to the person's side.
 */
export function isMachineActor(actor: string | null | undefined): boolean {
  return (
    actor?.startsWith("system:") === true || actor?.startsWith("auto:") === true
  );
}

/** A hotel's own site ready to render: `href` is absolute and http(s), `label`
 *  is the bare host an operator recognises the property by. */
export interface HotelWebsite {
  href: string;
  label: string;
}

const HAS_SCHEME = /^[a-z][a-z\d+.-]*:/i;

/**
 * A stored website value as a link, or null when there is nothing safe to open.
 *
 * The value is a platform snapshot — `tracking_hotel.website_url`, stamped from
 * the owner's `hotelWebsite` at enrollment — and this is where it becomes an
 * href in an operator's browser, so the render layer re-checks a field it does
 * not own rather than trusting the snapshot:
 *
 *  - No scheme ⇒ assume https. A bare `hotel.com` in an href resolves against
 *    the PORTAL's origin and navigates in-app instead of to the hotel.
 *  - A scheme is kept as written, so `mailto:` and `javascript:` reach the
 *    protocol check as themselves instead of being pasted behind an `https://`
 *    that would resolve them to some unrelated host.
 *  - Anything not http(s), or whose host carries no dot, is refused. Beside a
 *    real link the row falls back to the owner's email, which is honest; a
 *    link to nowhere is not.
 */
export function toHotelWebsite(
  raw: string | null | undefined,
): HotelWebsite | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(
      HAS_SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`,
    );
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (!url.hostname.includes(".")) return null;
    return { href: url.href, label: url.host.replace(/^www\./, "") };
  } catch {
    return null;
  }
}

/** Relative age suffix: `· 15d`. Empty when unknown. */
export function fmtAgeSuffix(days: number | null | undefined): string {
  if (days == null) return "";
  return `· ${days}d`;
}

/** Whole days since an ISO timestamp, floored at 0. */
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

/** Pull a human sentence off an untyped evidence blob, defensively — some
 *  evidence fields ship as a string, some as an object carrying one. */
export function evidenceSentence(evidence: unknown): string | null {
  if (typeof evidence === "string") return evidence;
  if (evidence && typeof evidence === "object") {
    const record = evidence as Record<string, unknown>;
    for (const key of ["evidence", "sentence", "summary", "message"]) {
      if (typeof record[key] === "string") return record[key] as string;
    }
  }
  return null;
}

/** Whole days since a `YYYY-MM-DD` day (or ISO timestamp), floored at 0. */
export function daysSinceDay(day: string | null | undefined): number | null {
  return daysSince(day);
}

/** `1,284` — whole-number metric. Null = the source has no data (em-dash). */
export function fmtNum(value: number | null | undefined): string {
  if (value == null) return "—";
  return Math.round(value).toLocaleString();
}

/** `3.4%` — ratio as percent; null = undefined ratio (no denominator). */
export function fmtPct(ratio: number | null | undefined, decimals = 0): string {
  if (ratio == null || !Number.isFinite(ratio)) return "—";
  return `${(ratio * 100).toFixed(decimals)}%`;
}

/** `$1,840` — whole-dollar money (spec §5.1 SPEND 30D format). */
export function fmtMoney(
  amount: number | null | undefined,
  currency?: string | null,
): string {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${Math.round(amount).toLocaleString()}`;
  }
}
